'use client';

/**
 * ui-future carousel physics — VERBATIM port of Control Room v4
 * hangWheel + _startPhys + glideToIndex:
 * - wheel gate: |dx| >= 2 AND |dx| > |dy|*1.15 (horizontal intent only)
 * - per-event velocity v = clamp(dx/320, -.28, .28); vel = vel*.55 + v*.75;
 *   pos += v (clamped 1:1 tracking while streaming)
 * - ONE continuous physics rAF loop; "streaming" window = 90ms since last
 *   wheel event (loop idles while wheel drives position)
 * - momentum: vel *= .94/frame; when |vel| < .012 → snap target = round(pos)
 * - snap: lerp .17 toward target, settle at |d| < .004
 * - glideToIndex: vel=0, snapT=target (shortest wrapped path) — same loop,
 *   never a second rAF
 * - drag ≥80px = flick (momentum carries); commit-on-release taps ignored
 *   within 300ms of a flick
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const CARD_PX = 320;
const FRICTION = 0.94;
const SNAP_LERP = 0.17;
const VEL_STOP = 0.012;
const SNAP_EPS = 0.004;
const V_CLAMP = 0.28;
const STREAM_MS = 90;

export function useCarousel(count: number, onPick: (i: number) => void) {
  const [pos, setPos] = useState(0);          // float position (card units)
  // v4 spec scrub law: transitions OFF while dragging, while the physics loop
  // runs, or within 140ms of a wheel event — the loop drives frames 1:1.
  const [scrubbing, setScrubbing] = useState(false);
  const st = useRef({
    pos: 0, vel: 0, raf: 0, down: false, downX: 0, downPos: 0,
    travel: 0, lastFlick: 0, lastWheelTs: 0,
    snapT: null as number | null, physOn: false,
  });

  const wrap = useCallback((p: number) => ((p % count) + count) % count, [count]);
  const focus = wrap(Math.round(st.current.pos));

  /* the ONE physics loop — v4 _startPhys verbatim */
  const startPhys = useCallback(() => {
    const s = st.current;
    if (s.physOn) return;
    s.physOn = true;
    setScrubbing(true);
    const stop = () => {
      s.physOn = false;
      // hold scrub until the 140ms wheel window has fully passed (spec line 1712)
      setTimeout(() => { if (!s.physOn && !s.down) setScrubbing(false); }, 140);
    };
    const step = () => {
      if (!s.physOn) return;
      if (s.down) { s.physOn = false; return; }
      const streaming = Date.now() - s.lastWheelTs < STREAM_MS;
      if (!streaming) {
        if (s.snapT == null) {
          s.vel *= FRICTION;
          s.pos += s.vel;
          if (Math.abs(s.vel) < VEL_STOP) s.snapT = Math.round(s.pos);
          setPos(wrap(s.pos));
        } else {
          const t = s.snapT;
          s.pos += (t - s.pos) * SNAP_LERP;
          if (Math.abs(t - s.pos) < SNAP_EPS) {
            s.pos = t;
            setPos(wrap(s.pos));
            s.snapT = null; s.vel = 0;
            stop();
            return;
          }
          setPos(wrap(s.pos));
        }
      }
      s.raf = requestAnimationFrame(step);
    };
    s.raf = requestAnimationFrame(step);
  }, [wrap]);

  /* glideToIndex verbatim: shortest wrapped path, snap via the same loop */
  const goTo = useCallback((i: number) => {
    const s = st.current;
    const curMod = wrap(s.pos);
    let dd = i - curMod;
    if (dd > count / 2) dd -= count;
    if (dd < -count / 2) dd += count;
    s.vel = 0;
    s.snapT = s.pos + dd;
    s.lastWheelTs = 0;
    startPhys();
  }, [count, wrap, startPhys]);

  const step = useCallback((dir: number) => {
    const s = st.current;
    s.lastFlick = Date.now();
    s.vel = 0;
    s.snapT = Math.round(s.pos) + dir;
    s.lastWheelTs = 0;
    startPhys();
  }, [startPhys]);

  /* pointer drag — v4 t4DragStart/t4DragEnd VERBATIM: drag ≥80px = discrete
     one-card flick on release (NOT a live scrub); pre-snap if physics is idle */
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const s = st.current;
    s.downX = e.clientX;
    s.vel = 0;
    if (s.physOn && s.snapT == null) s.snapT = Math.round(s.pos);
  }, []);

  const onPointerMove = useCallback(() => { /* spec: no live scrub on drag */ }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    const s = st.current;
    const d = e.clientX - s.downX;
    s.travel = Math.abs(d);
    if (d < -80 || d > 80) {
      s.lastFlick = Date.now();
      step(d < 0 ? 1 : -1);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* commit-on-release tap — spec _clickOk: travel ≤80 and not within 300ms of a flick */
  const tap = useCallback((cardIndex: number) => {
    const s = st.current;
    if (s.travel > 80) { s.travel = 0; return; }
    if (Date.now() - s.lastFlick < 300) return;
    onPick(cardIndex);
  }, [onPick]);

  /* horizontal trackpad wheel — hangWheel verbatim.
     Accepts native OR React wheel events: bind via a NON-PASSIVE native
     listener so preventDefault() can block the browser's two-finger
     back/forward history swipe (React onWheel is passive — can't). */
  const onWheel = useCallback((e: WheelEvent | React.WheelEvent) => {
    const ax = Math.abs(e.deltaX), ay = Math.abs(e.deltaY);
    if (ax > ay && (e as WheelEvent).cancelable !== false) e.preventDefault?.();
    if (ax < 2 || ax <= ay * 1.15) return;
    const s = st.current;
    s.lastWheelTs = Date.now();
    s.snapT = null;
    s.lastFlick = Date.now();
    const v = Math.max(-V_CLAMP, Math.min(V_CLAMP, e.deltaX / CARD_PX));
    s.vel = s.vel * 0.55 + v * 0.75;
    s.pos += v;
    setPos(wrap(s.pos));
    startPhys();
  }, [wrap, startPhys]);

  useEffect(() => () => {
    st.current.physOn = false;
    cancelAnimationFrame(st.current.raf);
  }, []);

  /* per-card transform: offset from center in wrapped card units */
  const offsetOf = useCallback((i: number) => {
    let off = i - pos;
    if (off > count / 2) off -= count;
    if (off < -count / 2) off += count;
    return off;
  }, [pos, count]);

  return { pos, focus, scrubbing, offsetOf, goTo, step, tap, onPointerDown, onPointerMove, onPointerUp, onWheel };
}

/* v4 hangar arc — VERBATIM from Control Room v4 t4Carousel wrapStyle:
   deg = ad<=2 ? ad*38 : 76+(ad-2)*7; left = 810+680·sinθ; top = 378+142·cosθ
   (1920×1080 stage coords, center card sits LOWEST); scale AS=[1.15,.88,.66,.52,.44];
   opacity AO=[1,.85,.45,.14,.05]; rotateY = sign·min(ad,2)·-12°; perspective 1800;
   origin 50% 100%. Dive: origin 50% 30%, scale(34), z 70. Arriving: 810/500 scale(.04). */
const AS = [1.15, .88, .66, .52, .44];
const AO = [1, .85, .45, .14, .05]; // Boss 2026-07-10: reverted one-card-only fade — he wants to SEE the side cards
export function cardStyle(off: number, opts?: { scrubbing?: boolean; diving?: boolean; arriving?: boolean }): React.CSSProperties {
  const d = off;
  const ad = Math.abs(d);
  const deg = ad <= 2 ? ad * 38 : 76 + (ad - 2) * 7;
  const th = Math.sign(d) * deg * Math.PI / 180;
  const az = Math.min(ad, 4), a0 = Math.floor(az), a1 = Math.min(a0 + 1, 4), at = az - a0;
  const sc = AS[a0] + (AS[a1] - AS[a0]) * at;
  const op = AO[a0] + (AO[a1] - AO[a0]) * at;
  const left = Math.round(790 + 700 * Math.sin(th));
  const top = Math.round(378 + 142 * Math.cos(th));
  if (opts?.diving && ad < .5) {
    return { position: 'absolute', left, top, width: 340, height: 406, transformOrigin: '50% 30%', transform: 'scale(34)', opacity: 0, zIndex: 70, transition: 'transform .92s cubic-bezier(.7,0,1,.45),opacity .16s linear .76s', pointerEvents: 'none' };
  }
  if (opts?.arriving) {
    return { position: 'absolute', left: 790, top: 500, width: 340, height: 406, transformOrigin: '50% 50%', transform: 'perspective(1800px) scale(.04)', opacity: 0, zIndex: Math.round(40 - ad * 10), transition: 'none' };
  }
  return {
    position: 'absolute', left, top, width: 340, height: 406, transformOrigin: '50% 100%',
    transform: `perspective(1800px) rotateY(${Math.sign(d) * Math.min(ad, 2) * -12}deg) scale(${sc})`,
    opacity: opts?.diving ? 0 : op,
    zIndex: Math.round(40 - ad * 10),
    transition: opts?.scrubbing ? 'opacity .3s' : 'left .55s cubic-bezier(.2,.75,.25,1),top .55s cubic-bezier(.2,.75,.25,1),transform .55s cubic-bezier(.2,.75,.25,1),opacity .45s ease-out',
  };
}
